import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cn, getPostType, getPostTypeString } from '../../../../utils';
import useDebounce from '../../../hooks/useDebounce';
import {
  getSearchSuggestionsAsync,
  setSearchSuggestions,
} from '../../../store/slices/market/marketSlice';
import SmoothLoader from '../../Common/Loader/SmoothLoader';

const MdClose = dynamic(() =>
  import('react-icons/md').then((mod) => mod.MdClose)
);
const MdSearch = dynamic(() =>
  import('react-icons/md').then((mod) => mod.MdSearch)
);

const InputWithButton = ({
  placeholder = 'Search by author, product title & product description',
  withPostType = false,
  postType = null,
  setShowPopover = () => {},
  searchText,
  setSearchText,
  isHovering,
  isRounded = false,
}) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { city } = router.query;
  const isMarket = router.pathname.includes('/market');
  const isSearchPage =
    router.pathname === '/market/search' ||
    router.pathname === '/community/search';

  const debouncedSearchText = useDebounce(searchText, 800);
  const searchloading = useSelector(({ market }) => market?.searchloading);

  useEffect(() => {
    if (debouncedSearchText) {
      dispatch(
        getSearchSuggestionsAsync({
          postType: getPostType(router.pathname),
          keyword: debouncedSearchText,
          discussion_type: postType ? postType : null,
          take: 5,
          skip: 0,
        })
      );
    }
  }, [debouncedSearchText, dispatch, postType, router.pathname]);

  const redirectToSearchPage = (keyword) => {
    isMarket
      ? router.push(
          `${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/search?postType=${postType?.id}&keyword=${keyword}&city=${city}`
        )
      : router.push(
          `${process.env.NEXT_PUBLIC_FRONTEND_URL}/${getPostTypeString(
            router.pathname
          )}/search?keyword=${keyword}&city=${city}`
        );
  };

  const handleOnChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleOnClick = () => {
    redirectToSearchPage(searchText);
  };

  const handleOnClear = () => {
    if (searchText) {
      if (isSearchPage) {
        setSearchText('');
        // redirectToSearchPage('');
      } else {
        setSearchText('');
      }

      dispatch(setSearchSuggestions(null));
    }
  };

  const handleOnBlur = () => {
    if (!isHovering) {
      setShowPopover(false);
      dispatch(setSearchSuggestions(null));
    }
  };

  const handleKeyDown = ({ key }) => {
    if (key === 'Enter') redirectToSearchPage(searchText);
  };

  useEffect(() => {
    dispatch(setSearchSuggestions(null));
  }, [dispatch]);

  return (
    <div
      className={cn(
        `flex items-center bg-light-grey`,
        withPostType ? 'w-full flex-1' : 'border',
        isRounded && 'h-[42px] rounded-[100px]'
      )}
    >
      <input
        type='text'
        className={cn(
          `w-full flex-1 truncate border-none px-[16px] text-[14px] outline-none placeholder:text-[14px] focus-within:ring-0 focus:ring-0`,
          isRounded
            ? '!h-[42px] w-[253px] rounded-bl-[100px] rounded-tl-[100px] xl:w-[343px]'
            : '!h-[48px] md:w-[343px]'
        )}
        placeholder={placeholder}
        value={searchText}
        onBlur={handleOnBlur}
        onFocus={() => {
          setShowPopover(true);
        }}
        onKeyDown={handleKeyDown}
        onChange={handleOnChange}
      />
      {searchloading ? (
        <div
          className={`${
            isRounded ? '!h-[42px] py-[12px]' : '!h-[48px]'
          } flex items-center bg-white px-4`}
        >
          <SmoothLoader className='h-[18px] w-[18px]' />
        </div>
      ) : (
        <button
          onClick={handleOnClear}
          className={`${
            isRounded ? '!h-[42px] py-[12px]' : '!h-[48px]'
          } bg-white px-4`}
        >
          {searchText && <MdClose className='text-[20px] text-[#000]' />}
        </button>
      )}

      <button
        disabled={searchloading}
        onClick={() => {
          handleOnClick();
        }}
        className={`${
          isRounded ? '!h-[42px] py-[12px]' : '!h-[48px]'
        }  border-l  px-4`}
      >
        <MdSearch className='text-[20px] text-[#000]' />
      </button>
    </div>
  );
};

export default InputWithButton;
